import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';

const StatsCard = ({
  icon: Icon,
  title,
  value,
  trend,
  trendValue,
  color = 'blue',
  subtitle,
  onClick,
  delay = 0
}) => {
  const isPositive = trend === 'up';

  const colorClasses = {
    blue: {
      bg: 'from-info-500 to-info-600',
      glow: 'shadow-[0_0_30px_rgba(59,130,246,0.3)]',
      border: 'border-info-500/20',
      iconBg: 'bg-info-500/10',
      iconText: 'text-info-500',
    },
    green: {
      bg: 'from-success-500 to-success-600',
      glow: 'shadow-[0_0_30px_rgba(16,185,129,0.3)]',
      border: 'border-success-500/20',
      iconBg: 'bg-success-500/10',
      iconText: 'text-success-500',
    },
    orange: {
      bg: 'from-secondary-500 to-secondary-600',
      glow: 'shadow-[0_0_30px_rgba(255,107,53,0.3)]',
      border: 'border-secondary-500/20',
      iconBg: 'bg-secondary-500/10',
      iconText: 'text-secondary-500',
    },
    purple: {
      bg: 'from-purple-500 to-purple-600',
      glow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]',
      border: 'border-purple-500/20',
      iconBg: 'bg-purple-500/10',
      iconText: 'text-purple-500',
    },
    yellow: {
      bg: 'from-accent-500 to-accent-600',
      glow: 'shadow-[0_0_30px_rgba(255,193,7,0.3)]',
      border: 'border-accent-500/20',
      iconBg: 'bg-accent-500/10',
      iconText: 'text-accent-500',
    },
    red: {
      bg: 'from-error-500 to-error-600',
      glow: 'shadow-[0_0_30px_rgba(239,68,68,0.3)]',
      border: 'border-error-500/20',
      iconBg: 'bg-error-500/10',
      iconText: 'text-error-500',
    },
  };

  const selectedColor = colorClasses[color] || colorClasses.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={cn(
        'group relative overflow-hidden',
        'bg-gradient-to-br from-bg-secondary to-bg-tertiary',
        'border rounded-xl',
        selectedColor.border,
        'transition-all duration-300',
        onClick && 'cursor-pointer',
        'hover:shadow-2xl',
        `hover:${selectedColor.glow}`
      )}
      onClick={onClick}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Animated background effect */}
      <motion.div
        className={cn(
          'absolute -top-24 -right-24 w-48 h-48 rounded-full',
          'bg-gradient-to-br opacity-5',
          selectedColor.bg,
          'blur-3xl'
        )}
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="relative p-6">
        {/* Header with icon */}
        <div className="flex items-start justify-between mb-4">
          <motion.div
            className={cn(
              'flex items-center justify-center',
              'w-14 h-14 rounded-xl',
              selectedColor.iconBg,
              'transition-transform duration-300',
              'group-hover:scale-110 group-hover:rotate-3'
            )}
            whileHover={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 0.5 }}
          >
            {typeof Icon === 'function' ? (
              <Icon size={28} className={selectedColor.iconText} strokeWidth={2.5} />
            ) : (
              <span className={cn('text-2xl', selectedColor.iconText)}>{Icon}</span>
            )}
          </motion.div>

          {/* Click indicator */}
          {onClick && (
            <motion.div
              className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-elevated opacity-0 group-hover:opacity-100 transition-opacity"
              whileHover={{ scale: 1.2 }}
            >
              <ArrowRight size={16} className="text-text-tertiary" />
            </motion.div>
          )}
        </div>

        {/* Value */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: delay + 0.1, duration: 0.4 }}
          className="mb-2"
        >
          <h3 className={cn(
            'text-3xl font-bold text-text-primary',
            'bg-gradient-to-r bg-clip-text',
            'transition-all duration-300'
          )}>
            {value}
          </h3>
        </motion.div>

        {/* Title */}
        <p className="text-sm font-medium text-text-secondary mb-1">
          {title}
        </p>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-xs text-text-tertiary mb-3">
            {subtitle}
          </p>
        )}

        {/* Trend indicator */}
        {trendValue && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay + 0.2, duration: 0.4 }}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full',
              'text-xs font-semibold',
              'backdrop-blur-sm',
              isPositive
                ? 'bg-success-500/10 text-success-500 border border-success-500/20'
                : 'bg-error-500/10 text-error-500 border border-error-500/20'
            )}
          >
            {isPositive ? (
              <TrendingUp size={14} className="animate-bounce" />
            ) : (
              <TrendingDown size={14} className="animate-bounce" />
            )}
            <span>{trendValue}</span>
          </motion.div>
        )}

        {/* Bottom gradient line */}
        <motion.div
          className={cn(
            'absolute bottom-0 left-0 right-0 h-1',
            'bg-gradient-to-r',
            selectedColor.bg,
            'opacity-0 group-hover:opacity-100',
            'transition-opacity duration-300'
          )}
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Shine effect on hover */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.05) 50%, transparent 100%)',
        }}
        animate={{
          x: ['-100%', '100%'],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      />
    </motion.div>
  );
};

export default StatsCard;
